"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Link from "next/link";
import { CalendarCheck, ChevronRight, PackageSearch, Sparkles, Tag, TrendingDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { OrganizationRanking } from "@/entities/organization/api/organizations";
import type { OrganizationMetricDrilldownTarget } from "@/entities/organization/ui/organization-metric-chip";
import { formatCount } from "@/shared/lib/format/numbers";
import { cn } from "@/shared/lib/utils";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/shared/ui/drawer";
function pct(value: number) {
  return `${Math.round(value * 100)}%`;
}
function rankToneText(rank: number) {
  if (rank === 1) return "text-amber-500 dark:text-amber-400";
  if (rank === 2) return "text-slate-500 dark:text-slate-300";
  if (rank === 3) return "text-orange-600 dark:text-orange-400";
  return "text-muted-foreground/80";
}
function scoreTone(value: number) {
  if (value >= 4) return "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  if (value >= 3) return "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300";
  if (value >= 2) return "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";
  return "border-border bg-muted/40 text-muted-foreground";
}
function ScoreCell(t0) {
  const $ = _c(12);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = cn("flex flex-col items-center gap-0.5 rounded-md border px-1.5 py-1", scoreTone(value));
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = value.toFixed(1);
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-base leading-none font-semibold tabular-nums">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label) {
    t4 = <span className="text-[9px] leading-none tracking-wide uppercase opacity-80">{label}</span>;
    $[6] = label;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className={t1}>{t3}{t4}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export function OrganizationRankingRowMobile({
  rank,
  ranking,
  onMetricClick
}: {
  rank: number;
  ranking: OrganizationRanking;
  onMetricClick?: (target: OrganizationMetricDrilldownTarget) => void;
}) {
  const {
    t
  } = useTranslation();
  const [metricsOpen, setMetricsOpen] = useState(false);
  const {
    metrics,
    ratios,
    score
  } = ranking;
  const location = ranking.city ?? ranking.region ?? "—";
  const cheapestPct = pct(ratios.cheapest_pct);
  const promotionPct = pct(ratios.promotion_pct);
  const content = <div className="pointer-events-none flex flex-col gap-4 px-4 py-3">
      <div className="flex items-center gap-3">
        <div className={cn("w-10 shrink-0 text-center text-3xl leading-none font-bold tabular-nums", rankToneText(rank))}>
          {rank}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-sm leading-snug font-semibold">{ranking.name}</p>
          <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-[11px]">
            <span className="truncate capitalize">{location}</span>
            {onMetricClick ? <>
                <span aria-hidden>·</span>
                <button type="button" onClick={() => setMetricsOpen(true)} className="text-primary hover:text-primary/80 focus-visible:ring-ring/40 pointer-events-auto relative z-10 -mx-0.5 inline-flex shrink-0 items-center gap-0.5 rounded px-0.5 font-medium focus:outline-none focus-visible:ring-2">
                  {t("organizations.ratings.seeMetrics")}
                  <ChevronRight className="size-3" />
                </button>
              </> : null}
          </div>
        </div>

        <div className="border-border bg-muted/60 flex shrink-0 flex-col items-center rounded-md border px-2 py-1 leading-none">
          <span className="text-foreground text-2xl font-bold tabular-nums">{score.overall.toFixed(1)}</span>
          <span className="text-muted-foreground mt-1 text-[9px] tracking-wide uppercase">
            {t("organizations.ratings.sortOptions.overall")}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2">
        <ScoreCell label={t("organizations.ratings.scoreLabels.price")} value={score.price} />
        <ScoreCell label={t("organizations.ratings.scoreLabels.variety")} value={score.variety} />
        <ScoreCell label={t("organizations.ratings.scoreLabels.medicines")} value={score.medicines} />
        <ScoreCell label={t("organizations.ratings.scoreLabels.freshness")} value={score.freshness} />
        <ScoreCell label={t("organizations.ratings.scoreLabels.promotions")} value={score.promotion} />
      </div>
      {!onMetricClick ? <div className="grid grid-cols-2 gap-x-3">
          <MetricRows />
        </div> : null}
    </div>;
  function MetricRows() {
    return <>
        <MetricRow icon={PackageSearch} label={t("organizations.ratings.metrics.medicines", {
        count: formatCount(metrics.distinct_medicines)
      })} />
        <MetricRow icon={Sparkles} label={t("organizations.ratings.metrics.exclusiveMedicines", {
        count: formatCount(metrics.exclusive_medicines)
      })} disabled={metrics.exclusive_medicines <= 0} onClick={onMetricClick ? () => {
        setMetricsOpen(false);
        onMetricClick({
          organizationId: ranking.organization_id,
          organizationName: ranking.name,
          metric: "exclusive",
          expectedCount: metrics.exclusive_medicines,
          expectedPctLabel: null
        });
      } : undefined} />
        <MetricRow icon={TrendingDown} label={t("organizations.ratings.metrics.cheapest", {
        pct: cheapestPct
      })} disabled={metrics.cheapest_count <= 0} onClick={onMetricClick ? () => {
        setMetricsOpen(false);
        onMetricClick({
          organizationId: ranking.organization_id,
          organizationName: ranking.name,
          metric: "cheapest",
          expectedCount: metrics.cheapest_count,
          expectedPctLabel: cheapestPct
        });
      } : undefined} />
        <MetricRow icon={Tag} label={t("organizations.ratings.metrics.promo", {
        pct: promotionPct
      })} disabled={metrics.promotion_count <= 0} onClick={onMetricClick ? () => {
        setMetricsOpen(false);
        onMetricClick({
          organizationId: ranking.organization_id,
          organizationName: ranking.name,
          metric: "promotion",
          expectedCount: metrics.promotion_count,
          expectedPctLabel: promotionPct
        });
      } : undefined} />
        <MetricRow icon={CalendarCheck} label={t("organizations.ratings.metrics.daysActive", {
        days: metrics.active_days
      })} />
      </>;
  }
  const metricsDrawer = onMetricClick ? <Drawer open={metricsOpen} onOpenChange={setMetricsOpen}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{ranking.name}</DrawerTitle>
          <DrawerDescription className="capitalize">{location}</DrawerDescription>
        </DrawerHeader>
        <div className="flex flex-col gap-1 px-4 pb-6">
          <MetricRows />
        </div>
      </DrawerContent>
    </Drawer> : null;
  return <>
      <div className="active:bg-muted/40 bg-card ring-foreground/8 relative overflow-hidden rounded-xl shadow-xs ring-1 transition-colors">
        {ranking.slug && onMetricClick ? <Link prefetch={false} href={`/app/organizations/${ranking.slug}`} aria-label={ranking.name} className="focus-visible:ring-ring/40 absolute inset-0 z-0 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset" /> : null}
        {content}
      </div>
      {metricsDrawer}
    </>;
}
function MetricRow(t0) {
  const $ = _c(11);
  const {
    icon: Icon,
    label,
    onClick,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon className="text-muted-foreground size-4 shrink-0" />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <span>{label}</span>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const content = t4;
  let t5;
  if ($[7] !== content || $[8] !== disabled || $[9] !== onClick) {
    t5 = onClick && !disabled ? <button type="button" onClick={onClick} className="text-foreground hover:bg-muted focus-visible:ring-primary/40 flex items-center gap-3 rounded-md py-2 text-left text-sm focus-visible:ring-2 focus-visible:outline-none">{content}</button> : <div className="text-foreground flex items-center gap-3 py-2 text-sm">{content}</div>;
    $[7] = content;
    $[8] = disabled;
    $[9] = onClick;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}